import { faroCoreApiClient } from "@api/faro-core-api-client";
import {
  AdmMember,
  BaseCompanyUserIdentityProps,
  BaseMemberIdProps,
} from "@custom-types/base-types";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { FaroCoreApiTypes } from "@stellar/core-api-client";

/** Fetch member data */
export const fetchMember = createAsyncThunk<
  AdmMember,
  Partial<BaseMemberIdProps>
>("member/fetchMember", async ({ memberId }) => {
  if (!memberId) {
    throw new Error("memberId is required to fetch member data");
  }

  return await faroCoreApiClient.admin.getMemberById({
    memberId,
  });
});

/** Remove member from company */
export const removeMemberFromCompany = createAsyncThunk<
  BaseCompanyUserIdentityProps,
  BaseCompanyUserIdentityProps
>("member/removeMemberFromCompany", async ({ userIdentity, companyId }) => {
  await faroCoreApiClient.admin.removeMemberFromCompany({
    companyId,
    userIdentity,
  });

  return { companyId, userIdentity };
});

/** Update member data */
export const updateMember = createAsyncThunk<
  FaroCoreApiTypes.UpdateMemberDetailsPayload,
  FaroCoreApiTypes.UpdateMemberDetailsPayload
>("member/updateMember", async (payload) => {
  await faroCoreApiClient.admin.updateMemberDetails(payload);

  return {
    ...payload,
  };
});

/** Props to change or reset the authentication method of a member */
type ChangeMemberAuthMethodProps = BaseMemberIdProps & {
  /** The authentication method the member should use */
  provider: FaroCoreApiTypes.AdminAuthMethod;

  /** Id of the workspace providing the SSO, only used when provider is `company-sso` */
  companyId: string;

  /** Whether the member's authentication method is reset instead of changed */
  shouldReset: boolean;
};

/** Change or reset the authentication method of a member */
export const changeMemberAuthMethod = createAsyncThunk<
  AdmMember,
  ChangeMemberAuthMethodProps
>(
  "member/changeMemberAuthMethod",
  async ({ memberId, provider, companyId, shouldReset }) => {
    if (shouldReset) {
      return await faroCoreApiClient.admin.resetAuthMethod({ memberId });
    }

    if (provider === "company-sso") {
      return await faroCoreApiClient.admin.changeAuthMethodToCompanySso({
        companyId,
        memberId,
      });
    }

    return await faroCoreApiClient.admin.changeAuthMethod({
      memberId,
      provider,
    });
  }
);

/** Fetch projects in which the member participates */
export const fetchMemberProjects = createAsyncThunk<
  FaroCoreApiTypes.AdmMembersProjects[],
  Partial<BaseMemberIdProps>
>("member/fetchMemberProjects", async ({ memberId }) => {
  if (!memberId) {
    throw new Error("memberId is required to fetch member projects");
  }

  return await faroCoreApiClient.admin.getProjectsOfMember({
    userIdentity: memberId,
  });
});
